import {
  GetMealQueryVariables,
  MealByHabitatQueryVariables,
  Meal as MealGraphql,
} from '@akin-home/amplify';
import { Meal } from '@akin-home/meals';
import {
  createMealMutation,
  deleteMealMutation,
  getMealQuery,
  listAllMealsByHabitatQuery,
  mealGraphqlToModel,
  mealModelToCreateGraphql,
  mealModelToUpdateGraphql,
  updateMealMutation,
} from '@akin-home/meals/frontend';
import {
  createCreateMutationHook,
  createDeleteMutationHook,
  createOptimisticUpdateMutationHook,
  createQueryKeySet,
  createUpdateMutationHook,
} from '@akin-home/utils/react-query';
import { useCallback } from 'react';
import { UseQueryOptions, useQuery } from 'react-query';

export * from './useGetMeal';
export * from './useSwapMealRecipe';

export const mealQueryKeys = createQueryKeySet({ key: 'meals' });

/** Hook to list meals by habitat */
export const useListMealsByHabitat = (
  params?: MealByHabitatQueryVariables,
  options?: UseQueryOptions<MealGraphql[], unknown, Meal[], any>
) =>
  useQuery(
    mealQueryKeys.list(params),
    () => listAllMealsByHabitatQuery(params),
    {
      select(data) {
        return data.map(mealGraphqlToModel);
      },
      enabled: !!params?.habitatID,
      ...options,
    }
  );

/** Handles delete meal mutation */
export const useDeleteMeal = createDeleteMutationHook({
  keySet: mealQueryKeys,
  mutationFunc: deleteMealMutation,
  keyExtractor: (_, vars) => vars.input?.id,
});

export const useQuickUpdateMeal = createOptimisticUpdateMutationHook({
  keySet: mealQueryKeys,
  mutationFunc: updateMealMutation,
  updater: ({ variables, item }) => ({ ...item, ...variables.input }),
  keyExtractor: (vars) => vars.input?.id,
});

export const useUpdateMeal = createUpdateMutationHook({
  keySet: mealQueryKeys,
  mutationFunc: updateMealMutation,
  keyExtractor: (data) => data.data.updateMeal?.id,
});

export const useCreateMeal = createCreateMutationHook({
  keySet: mealQueryKeys,
  mutationFunc: createMealMutation,
});

export const useUpdateMealFromModel = () => {
  const update = useUpdateMeal();

  return useCallback(
    async (meal: Meal) =>
      update.mutateAsync({ input: await mealModelToUpdateGraphql(meal) }),
    [update]
  );
};

export const useCreateMealFromModel = () => {
  const createEvent = useCreateMeal();

  return useCallback(
    async (meal: Meal) =>
      createEvent.mutateAsync({ input: await mealModelToCreateGraphql(meal) }),
    [createEvent]
  );
};

export const useGetMealQuery = (
  { id }: GetMealQueryVariables,
  options?: UseQueryOptions<MealGraphql, unknown, MealGraphql, any>
) =>
  useQuery(
    mealQueryKeys.detail(id),
    async () => (await getMealQuery({ id })).data.getMeal as MealGraphql,
    options
  );
